import {useUpdatedRef} from '@sail/react';
import {noop, once} from '@sail/utils';
import {useMemo} from 'react';
import {UninitializedObservabilityProviderError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {useObservabilityApi} from 'src/internal/provider/ObservabilityApiProvider';

import type {IAnalytics} from 'src/internal/analytics/types';

const logUninitializedErrorOnce = once(() => {
  logErrorInDev(new UninitializedObservabilityProviderError('useAnalytics'));
});

// WeakMap to store analytics objects by provider instance
const providerAnalyticsMap = new WeakMap<
  NonNullable<ReturnType<typeof useObservabilityApi>>,
  IAnalytics
>();

const defaultAnalytics: IAnalytics = {
  action: noop,
  link: noop,
  modal: noop,
  track: noop,
  viewed: noop,
};

/**
 * React hook to report contextualized analytics events.
 *
 * Returns an object with the analytics methods contextualized with the
 * service, release, user ID, and analytics configuration provided by the
 * closest `<ObservabilityProvider />`.
 *
 * @example Basic {{include "./examples/useAnalytics.basic.tsx"}}
 *
 * @see https://sail.stripe.me/apis/observability/useAnalytics
 */
export default function useAnalytics(): IAnalytics {
  const api = useObservabilityApi();
  const apiRef = useUpdatedRef(api);

  return useMemo((): IAnalytics => {
    const currentApiValue = apiRef.current;

    if (!currentApiValue) {
      logUninitializedErrorOnce();

      return defaultAnalytics;
    }

    // Check if we already have analytics for this provider
    let analytics = providerAnalyticsMap.get(currentApiValue);

    if (!analytics) {
      analytics = {
        action(...args: Parameters<IAnalytics['action']>): void {
          return apiRef.current?.analytics?.action(...args);
        },

        link(...args: Parameters<IAnalytics['link']>): void {
          return apiRef.current?.analytics?.link(...args);
        },

        modal(...args: Parameters<IAnalytics['modal']>): void {
          return apiRef.current?.analytics?.modal(...args);
        },

        track(...args: Parameters<IAnalytics['track']>): void {
          return apiRef.current?.analytics?.track(...args);
        },

        viewed(...args: Parameters<IAnalytics['viewed']>): void {
          return apiRef.current?.analytics?.viewed(...args);
        },
      } as IAnalytics;

      providerAnalyticsMap.set(currentApiValue, analytics);
    }

    return analytics;
  }, [
    apiRef, // This is a ref object, so it never changes and thus the returned object is stable.
  ]);
}
